/**
 * DialogCloseTitle —— 统一对话框标题栏（右上角关闭按钮）
 * 全站所有对话框统一复用此组件（docs/ui_dialog 模板 01）：
 * - 标题在左、关闭按钮（Dismiss20Regular）固定在右上角；
 * - 关闭按钮仅负责关闭（受控，由 onClose 决定如何置 open=false）；
 * - 底部不再放置"关闭 / 取消"这类纯关闭按钮，DialogActions 只保留动作按钮。
 */
import type { ReactNode } from 'react'
import { DialogTitle, Button, makeStyles } from '@fluentui/react-components'
import { Dismiss20Regular } from '@fluentui/react-icons'

const useStyles = makeStyles({
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: '8px',
  },
  titleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    flex: 1,
  },
  closeButton: {
    flexShrink: 0,
  },
})

export interface DialogCloseTitleProps {
  /** 标题文本或节点 */
  title: ReactNode
  /** 点击右上角关闭按钮时调用（由调用方负责把 open 置为 false） */
  onClose: () => void
  /** 标题前可选图标 */
  icon?: ReactNode
  /** 标题后的附加内容（如状态点、计数角标等），位于标题与关闭按钮之间 */
  extra?: ReactNode
  /** 关闭按钮的无障碍标签，默认"关闭" */
  closeAriaLabel?: string
}

export function DialogCloseTitle({
  title,
  onClose,
  icon,
  extra,
  closeAriaLabel = '关闭',
}: DialogCloseTitleProps) {
  const styles = useStyles()
  return (
    <DialogTitle>
      <div className={styles.titleRow}>
        <span className={styles.titleLeft}>
          {icon}
          {title}
          {extra}
        </span>
        <Button
          appearance="subtle"
          size="small"
          className={styles.closeButton}
          icon={<Dismiss20Regular />}
          aria-label={closeAriaLabel}
          onClick={onClose}
        />
      </div>
    </DialogTitle>
  )
}
